import React, { useContext, useEffect, useState } from 'react';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import JobLogButton from '../../components/job-log-button';
import JobRunButton from '../../components/job-run-button';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  apiRequestError, canReadPendingEnrollment, formatDateTimeForDataGrid, getBreadcrumbMaxItems,
  getPendingEnrollmentCompanyBatchItems, goToRoutePage, isNullOrWhiteSpace, setWindowTitle
} from '../../utils/helper';
import { MoreIcon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Grid, Hidden, IconButton, Link, ListItemIcon, ListItemText, Menu,
  MenuItem, Tooltip, Typography
} from '../../utils/material-ui-core';
import BatchItemReview from './crud/batchItemReview';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const BatchItem = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { showAlert, pendingEnrollmentCompanies, refreshData, userFunctionAccess } = useContext(AppContext);

  const { companyKey, batchLoadKey, batchLoadId } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [batchItemData, setBatchItemData] = useState<PendingEnrollmentCompanyBatchItem[]>([
    {
      assigned: "",
      batchLoadItemKey: "",
      createdDate: "",
      accountNumber: "",
      accountName: "",
      address: {
        addressline1: "",
        addressline2: "",
        addressline3: "",
        country: "",
        locality1: "",
        locality2: "",
        postalCode: "",
        region1: "",
        region2: "",
      },
      errorMessage: "",
      batchFlowTaskName: "",
      inQueue: false,
      onHold: false,
      retryCount: 0,
    },
  ]);
  const [batchLoadItemReviewDialogOpen, setBatchLoadItemReviewDialogOpen] = useState<boolean>(false);
  const [batchItemKeyProps, setBatchItemKeyProps] = useState({ batchLoadItemKey: "" });
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);
  const columns = [
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 200,
      type: "dateTime",
      valueFormatter: (params: GridValueFormatterParams) => formatDateTimeForDataGrid(params.value as string),
    },
    { field: "accountNumber", headerName: "Account Number", width: 200 },
    { field: "accountName", headerName: "Account Name", width: 250 },
    {
      field: "addressline1",
      headerName: "Address Line 1",
      width: 200,
      hide: true,
    },
    {
      field: "addressline2",
      headerName: "Address Line 2",
      width: 200,
      hide: true,
    },
    { field: "locality1", headerName: "Locality 1", width: 150, hide: true },
    { field: "region1", headerName: "Region 1", width: 150, hide: true },
    { field: "postalCode", headerName: "Postal Code", width: 150, hide: true },
    { field: "country", headerName: "Country", width: 150, hide: true },
    { field: "inQueue", headerName: "inQueue", width: 150, type: "boolean" },
    { field: "onHold", headerName: "On Hold", width: 150, type: "boolean" },
    { field: "retryCount", headerName: "Retry", width: 150, type: "number" },
    { field: "assigned", headerName: "Assigned", width: 250, hide: true },
    {
      field: "batchFlowTaskName",
      headerName: "Current State",
      width: 400,
      hide: true,
    },
    { field: "errorMessage", headerName: "errorMessage", width: 700 },
    {
      field: "batchLoadItemKey",
      headerName: "batchLoadItemKey",
      width: 250,
      hide: true,
    },
  ];

  // More menu click event
  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  // Close more menu
  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  const batchLoadItemReviewDialogResult = (confirmOutput: ConfirmOutput) => {
    setBatchLoadItemReviewDialogOpen(false);
    if (confirmOutput.status === "refresh") {
      refresh();
    }
  };

  const gotoCompany = () => {
    goToRoutePage("enrollmentcorrection/companies/");
  };

  const gotoBatches = () => {
    goToRoutePage("enrollmentcorrection/companies/" + companyKey + "/batches");
  };

  const refresh = async () => {
    setLoading(true);
    getData();
    refreshData("pec");
  };

  const getData = async () => {
    try {
      setLoading(true);
      setBatchItemData([]);
      const apiResponse = await getPendingEnrollmentCompanyBatchItems(companyKey, batchLoadKey);

      if (apiResponse.success) {
        setBatchItemData(apiResponse.data.pendingEnrollmentCompanyBatchItems);
        setLoading(false);
      } else {
        setLoading(false);
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const handleDataGridClick = async (params: GridRowParams, event: any) => {
    setBatchItemKeyProps({ batchLoadItemKey: params.row.batchLoadItemKey });
    setBatchLoadItemReviewDialogOpen(true);
  };

  useEffect(() => {
    setWindowTitle("Enrollment Correction - Batch - Items");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (!canReadPendingEnrollment(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (isNullOrWhiteSpace(companyKey)) {
        showAlert({ message: "Company key missing.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      getData();
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyKey]);

  return (
    <>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <BatchItemReview open={batchLoadItemReviewDialogOpen} keys={batchItemKeyProps} onCallback={batchLoadItemReviewDialogResult} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs maxItems={getBreadcrumbMaxItems(4)}>
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Enrollment Correction</Typography>
                    <Link color="inherit" onClick={() => gotoCompany()} className={globalStyles.link}>
                      Companies
                    </Link>
                    {pendingEnrollmentCompanies
                      .filter((pendingEnrollmentCompany) => pendingEnrollmentCompany.companyKey === companyKey)
                      .map((pendingEnrollmentCompany, index) => (
                        <Link color="inherit" key={index} onClick={() => gotoBatches()} className={globalStyles.link}>
                          {pendingEnrollmentCompany.companyName}
                        </Link>
                      ))}
                    <Typography color="textPrimary">{batchLoadId}</Typography>
                    <Typography color="textPrimary">Items</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <JobLogButton jobPrefix='enrollment' jobName='Enrollment' mode={0} handleGridMoreMenuClose={() => { }} />
                    <JobRunButton jobName={"Enrollment"} mode={0} fileId={batchLoadId} />

                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refresh()} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Hidden>

                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>

                    <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>

                      <JobLogButton jobPrefix='enrollment' jobName='Enrollment' mode={1} handleGridMoreMenuClose={handleGridMoreMenuClose} />

                      <JobRunButton jobName={"Enrollment"} mode={2} fileId={batchLoadId} />
                      <StyledMenuItem
                        onClick={(e) => {
                          refresh();
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <RefreshIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Refresh"} />
                      </StyledMenuItem>
                    </Menu>
                  </Box>
                </Hidden>
              </Grid>
            </Grid>

            <JobRunButton jobName={"New Enrollment"} mode={1} fileId={batchLoadId} />

            <Box className={pageStyles.datagridContainer}>
              <CustomDataGrid uniqueGridCode={"PendingEnrollmentCompanyBatchItemsResult"} columns={columns} rows={batchItemData} showToolBar={true} sortModel={[{ field: "createdDate", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
            </Box>
          </Box>
        </Screen>
      )}
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      height: "70vh",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default BatchItem;
